import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "../firebase.config";
import { PDFDocument } from "pdf-lib";

// Your hosted, fillable template PDF
const TEMPLATE_URL =
  "https://www.mlinc.collinicoding.com/ComplyOfficeExhibit.pdf";

// ---------- helpers ----------
const slugify = (s = "") =>
  String(s)
    .trim()
    .toLowerCase()
    .replace(/[^\w\- ]+/g, "")
    .replace(/\s+/g, "-")
    .slice(0, 120);

const monthName = (d) => d.toLocaleString("en-US", { month: "long" });

function pathFromDownloadUrl(url) {
  const after = url?.split("/o/")[1];
  if (!after) return null;
  const encoded = after.split("?")[0];
  return decodeURIComponent(encoded);
}

async function uploadToPath({ blobOrFile, fullPath, contentType }) {
  const ref = storageRef(storage, fullPath);
  await uploadBytes(ref, blobOrFile, contentType ? { contentType } : undefined);
  return await getDownloadURL(ref);
}

async function deleteByUrl(url) {
  const path = pathFromDownloadUrl(url);
  if (!path) return;
  try {
    await deleteObject(storageRef(storage, path));
  } catch (e) {
    // not fatal if already removed
    console.warn("[fillForm] deleteByUrl:", e);
  }
}

// ---------- PDF generation from your fillable template ----------
async function buildPdfFromTemplate(
  exhibitDataForm,
  { userCompany, signerName, signedDate }
) {
  const formPdfBytes = await fetch(TEMPLATE_URL).then((r) => r.arrayBuffer());
  const pdfDoc = await PDFDocument.load(formPdfBytes);
  const form = pdfDoc.getForm();

  // Text fields (exact field names from your template)
  form
    .getTextField("Service Provider Lessor")
    .setText(exhibitDataForm.serviceProvider || "");
  form
    .getTextField("Common/Branded Name")
    .setText(exhibitDataForm.commonName || "");
  form
    .getTextField("Location Address")
    .setText(exhibitDataForm.locationAddress || "");
  form.getTextField("City").setText(exhibitDataForm.city || "");
  form.getTextField("State").setText(exhibitDataForm.state || "");
  form
    .getTextField("Zip Code")
    .setText((exhibitDataForm.zipcode ?? "").toString());
  form
    .getTextField("Square Feet Leased")
    .setText((exhibitDataForm.sqftLeased ?? "").toString());
  form
    .getTextField("Additional Description")
    .setText(exhibitDataForm.description || "");
  form
    .getTextField("Expected Start Date")
    .setText(exhibitDataForm.startDate || "");
  form
    .getTextField("Expected Term of Lease")
    .setText(exhibitDataForm.term || "");
  form
    .getTextField("Number of Desks/Workstations")
    .setText((exhibitDataForm.desks ?? "").toString());

  // Company/signature metadata
  form.getTextField("Company Lessee").setText(userCompany || "");
  form.getField("Signature").setText(signerName || "");
  form.getField("Date Signed").setText(signedDate || "");

  // Radios / checkboxes
  switch (exhibitDataForm.propertyType) {
    case "Office":
      form.getCheckBox("Office").check();
      break;
    case "Industrial/Warehouse":
      form.getCheckBox("Industrial/Warehouse").check();
      break;
    case "Retail":
      form.getCheckBox("Retail").check();
      break;
    case "Mixed/Other":
      form.getCheckBox("Mixed/Other").check();
      break;
  }

  switch (exhibitDataForm.spaceType) {
    case "Private office":
      form.getCheckBox("Private Office").check();
      break;
    case "Cubicle":
    case "cubicle":
      form.getCheckBox("Cubicle").check();
      break;
  }

  switch (exhibitDataForm.settingType) {
    case "Interior":
      form.getCheckBox("Interior").check();
      break;
    case "Window":
      form.getCheckBox("Exterior/Window").check();
      break;
  }

  (exhibitDataForm.facility || []).forEach((v) => {
    if (v === "cam") form.getCheckBox("Common area maintenance").check();
    if (v === "furnished")
      form.getCheckBox("Furniture and furnishings").check();
    if (v === "janitorial") form.getCheckBox("Janitorial").check();
    if (v === "parking") form.getCheckBox("Parking").check();
    if (v === "tax") form.getCheckBox("Property insurance and taxes").check();
  });

  (exhibitDataForm.utility || []).forEach((v) => {
    if (v === "electricity") form.getCheckBox("Electricity").check();
    if (v === "gas") form.getCheckBox("Gas/Oil/Propane").check();
    if (v === "water") form.getCheckBox("Water").check();
    if (v === "phone") form.getCheckBox("Phone").check();
    if (v === "internet") form.getCheckBox("Internet/WiFi").check();
  });

  (exhibitDataForm.amenity || []).forEach((v) => {
    if (v === "meeting")
      form.getCheckBox("Furnished conference room(s) use").check();
    if (v === "kitchen") form.getCheckBox("Kitchen").check();
    if (v === "copy") form.getCheckBox("Copy/fax/printer/scanner use").check();
    if (v === "receptionist")
      form.getCheckBox("Receptionist/Answering").check();
  });

  // Make the filled fields visible in all viewers
  form.flatten();

  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes], { type: "application/pdf" });
}

// ---------- images sync & PDF upload ----------
async function syncImages({
  companyName,
  serviceProvider,
  exhibitId,
  newFiles = [], // File[]
  keepUrls = [], // URLs to keep (still selected)
  previousUrls = [], // URLs before edit (to detect deletions)
  now = new Date(),
}) {
  const year = now.getFullYear();
  const month = monthName(now); // "September"
  const baseDir = `${slugify(companyName)}/${slugify(
    serviceProvider
  )}/${year}/${slugify(month)}/images`;

  // remove any images that were unselected
  const toDelete = (previousUrls || []).filter((u) => !keepUrls.includes(u));
  await Promise.all(toDelete.map((u) => deleteByUrl(u)));

  // upload any newly added images
  const stamp = now.toISOString().replace(/[:.]/g, "-");
  const uploaded = await Promise.all(
    (newFiles || []).map(async (file, i) => {
      const fname = `${slugify(exhibitId || "new")}_${i}_${stamp}_${slugify(
        file.name || "image"
      )}`;
      const fullPath = `${baseDir}/${fname}`;
      return await uploadToPath({
        blobOrFile: file,
        fullPath,
        contentType: file.type || undefined,
      });
    })
  );

  return [...keepUrls, ...uploaded];
}

async function buildAndUploadPdf({
  exhibit,
  userCompany,
  signerName,
  signedDate,
  companyName,
  serviceProvider,
  exhibitId,
  previousPdfUrl, // existing pdfUrl (to delete)
  now = new Date(),
}) {
  // Fill your hosted template
  const pdfBlob = await buildPdfFromTemplate(exhibit, {
    userCompany,
    signerName,
    signedDate,
  });

  const year = now.getFullYear();
  const month = monthName(now);
  const stamp = now.toISOString().replace(/[:.]/g, "-");

  const baseDir = `${slugify(companyName)}/${slugify(
    serviceProvider
  )}/${year}/${slugify(month)}/pdfs`;
  const pdfName = `${slugify(exhibitId || "new")}_${stamp}.pdf`;
  const fullPath = `${baseDir}/${pdfName}`;

  // upload with correct content type so viewers can open it
  const pdfURL = await uploadToPath({
    blobOrFile: pdfBlob,
    fullPath,
    contentType: "application/pdf",
  });

  // clean up the old pdf (if any)
  await deleteByUrl(previousPdfUrl);

  return pdfURL;
}

/**
 * Main entry for your forms.
 * - Always (re)builds the PDF from the hosted template
 * - Uploads new images, deletes removed ones
 * - Returns { pdfURL, fileUrls }
 * This file DOES NOT touch Firestore. Do your single doc update in the form.
 */
export async function fillForm(exhibit, status, ctx = {}) {
  const {
    // identity/info you pass in from the component (don’t import Redux here)
    userCompany = "", // e.g., user.companyName
    signerName = "", // e.g., `${user.contactFirstName} ${user.contactLastName}`
    signedDate = "", // e.g., currentDate prop
    companyName = exhibit.serviceProvider
      ? exhibit.companyName || userCompany
      : userCompany,
    serviceProvider = exhibit.serviceProvider || "UnknownProvider",
    exhibitId = exhibit.id,
    previousPdfUrl = undefined,

    // images
    newImageFiles = [], // File[]
    keepImageUrls = [], // still-selected URLs
    previousImageUrls = [], // URLs before edit (for deletions)

    now = new Date(),
  } = ctx;

  // PDF
  const pdfURL = await buildAndUploadPdf({
    exhibit: { ...exhibit, status },
    userCompany,
    signerName,
    signedDate,
    companyName,
    serviceProvider,
    exhibitId,
    previousPdfUrl,
    now,
  });

  // Images
  const fileUrls = await syncImages({
    companyName,
    serviceProvider,
    exhibitId,
    newFiles: newImageFiles,
    keepUrls: keepImageUrls,
    previousUrls: previousImageUrls,
    now,
  });

  return { pdfURL, fileUrls };
}
